import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import cn from "classnames";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  otherKey,
  RankedOption,
  resolveSurveyCopy,
  SURVEY_QUESTIONS,
  SurveyAnswerId,
  SurveyAggregates,
  SurveyAnswers,
  SurveyStatus,
} from "../../common/postTrialSurvey";
import Button from "../common/Button";
import Reveal from "../common/Reveal";
import ConfirmModal from "../FeatureRequests/ConfirmModal";

type OfficerRow = {
  user_id: string;
  name: string | null;
  email: string;
  badge: string | null;
  rank: string | null;
  response_id: string | null;
  status: SurveyStatus | "not_started";
  trigger: string | null;
  platform: string | null;
  answers: SurveyAnswers;
  quote: string | null;
  dismissed_count: number;
  // Last "I'm still evaluating" click.
  last_dismissed_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  // This officer's own "Send survey" window, null when none is live.
  open_until: string | null;
};

// send = prompt officers who haven't answered; resend = also set aside the
// answers of those who have, so everyone selected is asked.
type OfficerSend = { mode: "send" | "resend"; officers: OfficerRow[] };

type SurveyData = {
  agency: { id: string; name: string };
  round: number;
  roundRequestedAt: string | null;
  // Staff-opened "Send survey now" window, null when none is live.
  openUntil: string | null;
  officers: OfficerRow[];
  aggregates: SurveyAggregates;
  previousRounds: { round: number; completed: number }[];
};

interface Props {
  agencyId: string;
  adminEmail: string;
  // Heading override (the results panel prefixes the agency name).
  title?: string;
}

const base = (id: string) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/agency-management/agencies/${id}/survey`;

const fmt = (n: number | null, suffix = "") => (n === null ? "—" : `${n}${suffix}`);

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const officerLabel = (o: OfficerRow) => o.name || o.email;

const answered = (o: OfficerRow) => o.status === "completed" || o.status === "skipped";

// An in-progress row with nothing answered that exists only because the
// officer pressed "I'm still evaluating".
const stillEvaluating = (o: OfficerRow) =>
  o.status === "in_progress" && o.dismissed_count > 0 && Object.keys(o.answers ?? {}).length === 0 && !o.quote;

const checkboxCls =
  "checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500";

const triggerLabel: Record<string, string> = {
  final_week: "final week",
  post_expiry: "after expiry",
  manual: "sent by staff",
};

const statusChip: Record<OfficerRow["status"], { label: string; cls: string }> = {
  completed: { label: "Completed", cls: "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300" },
  in_progress: { label: "In progress", cls: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300" },
  skipped: { label: "Skipped", cls: "bg-base-200 text-base-content/60" },
  not_started: { label: "Not asked yet", cls: "bg-base-200 text-base-content/50" },
};

// One officer's answers, question by question, shown when their row is
// expanded. Unanswered questions stay listed (dimmed) so a partial
// response reads as partial.
function OfficerAnswers({ officer, agencyName }: { officer: OfficerRow; agencyName: string }) {
  const display = (id: SurveyAnswerId): string | null => {
    const q = SURVEY_QUESTIONS.find((x) => x.id === id);
    const v = officer.answers[id];
    if (!q || v === undefined || v === null) return null;
    if (q.type === "scale") return `${v} / ${q.max}  (${q.minLabel} → ${q.maxLabel})`;
    if (q.type === "nps") return `${v} / 10`;
    if (q.type === "single" || q.type === "multi") {
      const values = Array.isArray(v) ? v : [String(v)];
      const other = officer.answers[otherKey(id)];
      return values
        .map((val) => {
          const opt = q.options.find((o) => o.value === val);
          const label = opt?.label ?? val;
          return opt?.allowText && typeof other === "string" && other.trim() ? `${label}: “${other.trim()}”` : label;
        })
        .join(", ");
    }
    return String(v);
  };

  return (
    <div className="px-4 pb-4 pt-1">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 rounded-xl border border-base-300 bg-base-200/50 p-4">
        {SURVEY_QUESTIONS.map((q, i) => {
          const prompt = resolveSurveyCopy(q, { mode: "trial", agencyName }).prompt;
          const value = q.type === "text" ? (officer.quote?.trim() ? `“${officer.quote.trim()}”` : null) : display(q.id as SurveyAnswerId);
          return (
            <div key={q.id} className={q.type === "text" ? "md:col-span-2" : undefined}>
              <dt className="text-[11px] text-base-content/50">
                {i + 1}. {prompt}
              </dt>
              <dd className={cn("text-sm mt-0.5", value ? "text-base-content" : "text-base-content/40 italic")}>
                {value ?? (q.type === "text" ? "No quote" : "Not answered")}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-base-300 bg-base-200/50 p-4">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-base-content/50">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-base-content">{value}</div>
      {sub && <div className="text-[11px] text-base-content/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function BarRow({ option, max }: { option: RankedOption; max: number }) {
  const width = max > 0 ? Math.round((option.count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-56 shrink-0 truncate text-base-content/80" title={option.label}>
        {option.label}
      </div>
      <div className="flex-1 h-2 rounded-full bg-base-300 overflow-hidden">
        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${width}%` }} />
      </div>
      <div className="w-20 shrink-0 text-right tabular-nums text-base-content/60">
        {option.count} · {option.pct}%
      </div>
    </div>
  );
}

function BarGroup({
  title,
  options,
  note,
  otherTexts,
}: {
  title: string;
  options: RankedOption[];
  note?: string;
  otherTexts?: string[];
}) {
  const max = Math.max(0, ...options.map((o) => o.count));
  return (
    <div className="rounded-xl border border-base-300 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="text-sm font-semibold text-base-content">{title}</h4>
        {note && <span className="text-[11px] text-base-content/50">{note}</span>}
      </div>
      <div className="space-y-2">
        {options.map((o) => (
          <BarRow key={o.value} option={o} max={max} />
        ))}
      </div>
      {otherTexts && otherTexts.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-base-300 pt-3 text-xs text-base-content/70">
          {otherTexts.map((t, i) => (
            <li key={i}>“{t}”</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const AgencySurveyTab: React.FC<Props> = ({ agencyId, adminEmail, title = "Officer survey" }) => {
  const [data, setData] = useState<SurveyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmSend, setConfirmSend] = useState<OfficerSend | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<OfficerRow[] | null>(null);
  const [resendAfterDelete, setResendAfterDelete] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [openDays, setOpenDays] = useState("30");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (userId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<SurveyData>(base(agencyId), {
        headers: { "x-admin-email": adminEmail },
      });
      setData(res.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to load survey responses");
    } finally {
      setLoading(false);
    }
  }, [agencyId, adminEmail]);

  useEffect(() => {
    void load();
  }, [load]);

  const requestAll = async () => {
    setBusy(true);
    try {
      await axios.post(`${base(agencyId)}/request-again`, { adminEmail });
      toast.success("Every officer will be asked again");
      setConfirmAll(false);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to request new responses");
    } finally {
      setBusy(false);
    }
  };

  const openSurvey = async () => {
    setBusy(true);
    try {
      await axios.post(`${base(agencyId)}/open`, { adminEmail, days: Number(openDays) });
      toast.success("Survey sent — every officer is prompted on their next visit");
      setConfirmOpen(false);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to send the survey");
    } finally {
      setBusy(false);
    }
  };

  const closeSurvey = async () => {
    setBusy(true);
    try {
      await axios.post(`${base(agencyId)}/close`, { adminEmail });
      toast.success("Survey closed");
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to close the survey");
    } finally {
      setBusy(false);
    }
  };

  const sendToOfficers = async () => {
    if (!confirmSend) return;
    const { mode, officers: targets } = confirmSend;
    setBusy(true);
    try {
      const res = await axios.post<{ sent: number; reasked: number; alreadyAnswered: number }>(
        `${base(agencyId)}/officers/send`,
        {
          adminEmail,
          userIds: targets.map((o) => o.user_id),
          days: Number(openDays),
          askAgain: mode === "resend",
        }
      );
      const { sent, alreadyAnswered } = res.data;
      const who = targets.length === 1 ? officerLabel(targets[0]) : `${sent} officers`;
      toast.success(
        alreadyAnswered > 0
          ? `Survey sent to ${who}. ${alreadyAnswered} already answered and ${alreadyAnswered === 1 ? "was" : "were"} skipped`
          : `Survey sent to ${who}`
      );
      setConfirmSend(null);
      setSelected(new Set());
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to send the survey");
    } finally {
      setBusy(false);
    }
  };

  const deleteResponses = async () => {
    if (!confirmDelete) return;
    const targets = confirmDelete;
    setBusy(true);
    try {
      const res = await axios.post<{ deleted: number }>(`${base(agencyId)}/officers/delete-responses`, {
        adminEmail,
        userIds: targets.map((o) => o.user_id),
        resend: resendAfterDelete,
        days: Number(openDays),
      });
      const who = targets.length === 1 ? `${officerLabel(targets[0])}'s response` : `${res.data.deleted} responses`;
      toast.success(resendAfterDelete ? `Deleted ${who} and sent the survey again` : `Deleted ${who}`);
      setConfirmDelete(null);
      setSelected(new Set());
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to delete the responses");
    } finally {
      setBusy(false);
    }
  };

  const stopAsking = async (targets: OfficerRow[]) => {
    setBusy(true);
    try {
      await axios.post(`${base(agencyId)}/officers/close`, {
        adminEmail,
        userIds: targets.map((o) => o.user_id),
      });
      toast.success(targets.length === 1 ? `Stopped asking ${officerLabel(targets[0])}` : `Stopped asking ${targets.length} officers`);
      setSelected(new Set());
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to close the survey");
    } finally {
      setBusy(false);
    }
  };

  const toggleSelected = (userId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  if (loading && !data) {
    return <div className="py-10 text-center text-sm text-base-content/50">Loading survey responses…</div>;
  }
  if (!data) return null;

  const { aggregates: a, officers } = data;
  const completed = officers.filter((o) => o.status === "completed").length;
  const timeSavedQ = SURVEY_QUESTIONS.find((q) => q.id === "time_saved_per_report");
  const selectedOfficers = officers.filter((o) => selected.has(o.user_id));
  const allSelected = officers.length > 0 && selectedOfficers.length === officers.length;
  const selectedOpen = selectedOfficers.filter((o) => o.open_until);
  const selectedWithResponse = selectedOfficers.filter((o) => o.response_id);
  const sendAnswered = confirmSend ? confirmSend.officers.filter(answered).length : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-base-content">{title}</h3>
          <p className="text-sm text-base-content/60 mt-0.5">
            Round {data.round}
            {data.roundRequestedAt
              ? ` · requested ${new Date(data.roundRequestedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
              : " · shown automatically in the final week of the trial and after it ends"}
            {data.previousRounds.length > 0 &&
              ` · earlier rounds: ${data.previousRounds.map((r) => `#${r.round} (${r.completed})`).join(", ")}`}
          </p>
          {data.openUntil && (
            <p className="mt-1 inline-flex items-center gap-2 text-xs">
              <span className="px-2 py-0.5 rounded-full font-semibold bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300">
                Survey open
              </span>
              <span className="text-base-content/60">
                every officer is prompted until{" "}
                {new Date(data.openUntil).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </span>
              <button type="button" onClick={closeSurvey} disabled={busy} className="text-blue-600 dark:text-blue-300 hover:underline font-medium">
                Close early
              </button>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${base(agencyId)}.csv`}
            target="_blank"
            rel="noreferrer"
            className="btn no-animation border-0 shadow-none !min-h-10 h-[25px] text-xs xl:text-sm px-2 inline-flex items-center"
          >
            Export CSV
          </a>
          <Button variant="secondary" size="sm" onClick={() => setConfirmAll(true)}>
            Request new responses
          </Button>
          {!data.openUntil && (
            <Button variant="primary" size="sm" onClick={() => setConfirmOpen(true)}>
              Send survey now
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Responses" value={`${completed} / ${officers.length}`} sub={a.started > completed ? `${a.started - completed} in progress` : "officers in the agency"} />
        <Tile label="Avg rating" value={fmt(a.avg_overall, " / 5")} sub="overall, 1–5" />
        <Tile label="NPS" value={fmt(a.nps.score)} sub={a.nps.answered ? `${a.nps.promoters} promoters · ${a.nps.detractors} detractors` : "no answers yet"} />
        <Tile label="Would keep it" value={fmt(a.keep.pct_yes_or_probably, "%")} sub="yes or probably" />
      </div>

      {a.responses > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile
              label="Open to AI"
              value={
                a.ai_attitude.pct_open_before !== null && a.ai_attitude.pct_open_after !== null
                  ? `${a.ai_attitude.pct_open_before}% → ${a.ai_attitude.pct_open_after}%`
                  : fmt(a.ai_attitude.pct_open_after, "%")
              }
              sub="before → after the trial"
            />
            <Tile
              label="Skeptics won over"
              value={
                a.ai_attitude.skeptics > 0
                  ? `${a.ai_attitude.skeptics_won_over} of ${a.ai_attitude.skeptics}`
                  : "—"
              }
              sub={a.ai_attitude.pct_skeptics_won_over !== null ? `${a.ai_attitude.pct_skeptics_won_over}% now open to it` : "nobody started skeptical"}
            />
            <Tile label="Reports better" value={fmt(a.pct_quality_better, "%")} sub="vs. writing without it" />
            <Tile label="Saved per report" value={fmt(a.time_saved.weighted_avg_min, " min")} sub="self-reported, weighted" />
            <Tile label="Less overtime" value={fmt(a.pct_overtime_reduced, "%")} sub="of officers who work it" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BarGroup title="Feelings about AI before the trial" options={a.ai_attitude.before} />
            <BarGroup title="Feelings about AI after the trial" options={a.ai_attitude.after} note={a.avg_ease !== null ? `ease of use ${a.avg_ease} / 5` : undefined} />
            {timeSavedQ && <BarGroup title="Time saved per report" options={a.time_saved.buckets} note={`${a.per_question.time_saved_per_report?.answered ?? 0} answered`} />}
            <BarGroup title="Most useful features" options={a.top_features} note="up to 4 each" />
            <BarGroup
              title="Where the time went"
              options={a.time_reinvested}
              otherTexts={a.per_question.time_reinvested?.other_texts}
            />
            <BarGroup
              title="What improved about reports"
              options={a.per_question.quality_improvements?.distribution ?? []}
              otherTexts={a.per_question.quality_improvements?.other_texts}
            />
            <BarGroup
              title="What held officers back"
              options={a.concerns}
              otherTexts={a.per_question.concerns?.other_texts}
            />
            <BarGroup title="Keep it?" options={a.keep.distribution} />
            <BarGroup title="Time to comfort" options={a.time_to_comfort} />
          </div>
        </>
      )}

      {a.quotes.length > 0 && (
        <div className="rounded-xl border border-base-300 p-4">
          <h4 className="text-sm font-semibold text-base-content mb-3">In their words</h4>
          <div className="space-y-3">
            {a.quotes.map((q, i) => (
              <Reveal key={i} index={i} className="rounded-lg bg-base-200/60 px-4 py-3">
                <p className="text-sm text-base-content/90 leading-relaxed">“{q.text}”</p>
                <div className="mt-1.5 text-[11px] text-base-content/60">
                  — {[q.rank, q.name].filter(Boolean).join(" ") || "Officer"}
                  {q.badge ? ` · #${q.badge}` : ""}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      {selectedOfficers.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-base-300 bg-base-200/50 px-4 py-2">
          <span className="text-sm font-medium text-base-content mr-auto">
            {selectedOfficers.length} officer{selectedOfficers.length === 1 ? "" : "s"} selected
          </span>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmSend({ mode: "send", officers: selectedOfficers })}>
            Send survey
          </Button>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmSend({ mode: "resend", officers: selectedOfficers })}>
            Resend survey
          </Button>
          {selectedWithResponse.length > 0 && (
            <Button variant="danger" size="sm" disabled={busy} onClick={() => setConfirmDelete(selectedWithResponse)}>
              Delete response{selectedWithResponse.length === 1 ? "" : "s"}
            </Button>
          )}
          {selectedOpen.length > 0 && (
            <Button variant="clean" size="sm" disabled={busy} onClick={() => stopAsking(selectedOpen)}>
              Stop asking
            </Button>
          )}
          <Button variant="clean" size="sm" disabled={busy} onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      )}

      <div className="rounded-xl border border-base-300 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-base-200/60 text-[11px] uppercase tracking-wider text-base-content/50">
            <tr>
              <th className="w-10 pl-4 py-2">
                <input
                  type="checkbox"
                  aria-label="Select every officer"
                  className={checkboxCls}
                  checked={allSelected}
                  onChange={() => setSelected(allSelected ? new Set() : new Set(officers.map((o) => o.user_id)))}
                />
              </th>
              <th className="text-left px-4 py-2 font-semibold">Officer</th>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
              <th className="text-left px-4 py-2 font-semibold">Submitted</th>
              <th className="text-left px-4 py-2 font-semibold">Via</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {officers.map((o, i) => {
              const chip = stillEvaluating(o)
                ? { label: "Still evaluating", cls: "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" }
                : statusChip[o.status];
              // Only rows with something answered expand.
              const hasAnswers = !!o.response_id && (Object.keys(o.answers ?? {}).length > 0 || !!o.quote);
              const isOpen = hasAnswers && expanded.has(o.user_id);
              return (
                <Fragment key={o.user_id}>
                <Reveal
                  as="tr"
                  index={i}
                  className={cn("border-t border-base-300", hasAnswers && "cursor-pointer hover:bg-base-200/60", isOpen && "bg-base-200/40")}
                  onClick={hasAnswers ? () => toggleExpanded(o.user_id) : undefined}
                  aria-expanded={hasAnswers ? isOpen : undefined}
                >
                  <td className="w-10 pl-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${officerLabel(o)}`}
                      className={checkboxCls}
                      checked={selected.has(o.user_id)}
                      onChange={() => toggleSelected(o.user_id)}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1 font-medium text-base-content">
                      {officerLabel(o)}
                      {hasAnswers && (
                        <ChevronRightIcon className={cn("size-3.5 text-base-content/40 transition-transform", isOpen && "rotate-90")} />
                      )}
                    </div>
                    <div className="text-[11px] text-base-content/50">
                      {[o.rank, o.badge ? `#${o.badge}` : null].filter(Boolean).join(" · ") || o.email}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={cn("px-2 py-0.5 rounded-full text-[11px] font-semibold", chip.cls)}>{chip.label}</span>
                    {o.status === "in_progress" && o.dismissed_count > 0 && (
                      <span className="ml-1 text-[11px] text-base-content/40">
                        postponed{o.dismissed_count > 1 ? ` ×${o.dismissed_count}` : ""}
                        {o.last_dismissed_at ? ` · ${shortDate(o.last_dismissed_at)}` : ""}
                      </span>
                    )}
                    {o.open_until && !answered(o) && (
                      <div className="mt-0.5 text-[11px] text-base-content/50">Sent · asked until {shortDate(o.open_until)}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-base-content/70 tabular-nums">
                    {o.completed_at ? shortDate(o.completed_at) : "—"}
                  </td>
                  <td className="px-4 py-2 text-base-content/60">
                    {o.platform ? `${o.platform}${o.trigger ? ` · ${triggerLabel[o.trigger] ?? o.trigger}` : ""}` : "—"}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1.5">
                      {o.response_id && (
                        <Button variant="danger" size="xs" disabled={busy} onClick={() => setConfirmDelete([o])}>
                          Delete
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="xs"
                        disabled={busy}
                        onClick={() => setConfirmSend({ mode: answered(o) ? "resend" : "send", officers: [o] })}
                      >
                        {answered(o) ? "Ask again" : o.open_until ? "Send again" : "Send survey"}
                      </Button>
                    </div>
                  </td>
                </Reveal>
                {isOpen && (
                  <tr className="bg-base-200/40">
                    <td colSpan={6}>
                      <OfficerAnswers officer={o} agencyName={data.agency.name} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {officers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-base-content/50">
                  No officers in this agency yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={confirmAll}
        title="Ask every officer again?"
        body={
          <>
            Starts round {data.round + 1}. Every officer at {data.agency.name} will see the survey the next time it applies (final week or after expiry), even if they already answered. Earlier responses are kept.
          </>
        }
        confirmLabel="Request new responses"
        busy={busy}
        onConfirm={requestAll}
        onClose={() => setConfirmAll(false)}
      />
      <ConfirmModal
        open={confirmOpen}
        title="Send the survey now?"
        body={
          <div className="space-y-3">
            <p>
              Every officer at {data.agency.name} will be required to complete the survey the next time they open Police Narratives AI, whether or not the agency is on a trial. Officers who already answered round {data.round} are skipped; use “Request new responses” first if you want them asked again.
            </p>
            <label className="flex items-center gap-2 text-sm">
              <span className="text-base-content/70">Keep it open for</span>
              <input
                type="number"
                min={1}
                max={365}
                value={openDays}
                onChange={(e) => setOpenDays(e.target.value)}
                className="w-20 rounded-lg border border-base-300 bg-base-100 px-2 py-1 text-sm text-base-content outline-none focus:border-blue-500"
              />
              <span className="text-base-content/70">days</span>
            </label>
          </div>
        }
        confirmLabel="Send survey"
        busy={busy}
        onConfirm={openSurvey}
        onClose={() => setConfirmOpen(false)}
      />
      <ConfirmModal
        open={!!confirmSend}
        title={
          confirmSend
            ? `${confirmSend.mode === "resend" ? "Resend the survey to" : "Send the survey to"} ${
                confirmSend.officers.length === 1
                  ? officerLabel(confirmSend.officers[0])
                  : `${confirmSend.officers.length} officers`
              }?`
            : ""
        }
        body={
          confirmSend && (
            <div className="space-y-3">
              <p>
                {confirmSend.officers.length === 1 ? "They" : "Only the selected officers"} will be required to complete the survey the next time they open Police Narratives AI, whether or not {data.agency.name} is on a trial. Nobody else at the department is prompted.
              </p>
              {sendAnswered > 0 && (
                <p>
                  {confirmSend.mode === "resend"
                    ? `${sendAnswered} of them already answered round ${data.round}. Their current response is set aside (kept as history) and they are asked again.`
                    : `${sendAnswered} of them already answered round ${data.round} and will be skipped. Use “Resend survey” to ask them again.`}
                </p>
              )}
              {confirmSend.officers.length > 1 && (
                <p className="text-xs text-base-content/60">
                  {confirmSend.officers.map(officerLabel).join(", ")}
                </p>
              )}
              <label className="flex items-center gap-2 text-sm">
                <span className="text-base-content/70">Keep it open for</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={openDays}
                  onChange={(e) => setOpenDays(e.target.value)}
                  className="w-20 rounded-lg border border-base-300 bg-base-100 px-2 py-1 text-sm text-base-content outline-none focus:border-blue-500"
                />
                <span className="text-base-content/70">days</span>
              </label>
            </div>
          )
        }
        confirmLabel={confirmSend?.mode === "resend" ? "Resend survey" : "Send survey"}
        busy={busy}
        onConfirm={sendToOfficers}
        onClose={() => setConfirmSend(null)}
      />
      <ConfirmModal
        open={!!confirmDelete}
        title={
          confirmDelete
            ? `Delete ${
                confirmDelete.length === 1
                  ? `${officerLabel(confirmDelete[0])}'s response`
                  : `${confirmDelete.length} responses`
              }?`
            : ""
        }
        body={
          confirmDelete && (
            <div className="space-y-3">
              <p>
                {confirmDelete.length === 1 ? "Their" : "These"} round {data.round} answers are removed from these results, the CSV, and every findings report. This can't be undone here; a copy is kept in the staff audit log.
              </p>
              {confirmDelete.length > 1 && (
                <p className="text-xs text-base-content/60">{confirmDelete.map(officerLabel).join(", ")}</p>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className={checkboxCls}
                  checked={resendAfterDelete}
                  onChange={(e) => setResendAfterDelete(e.target.checked)}
                />
                <span className="text-base-content/80">Send the survey to them again for</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={openDays}
                  disabled={!resendAfterDelete}
                  onChange={(e) => setOpenDays(e.target.value)}
                  className="w-20 rounded-lg border border-base-300 bg-base-100 px-2 py-1 text-sm text-base-content outline-none focus:border-blue-500 disabled:opacity-50"
                />
                <span className="text-base-content/80">days</span>
              </label>
              <p className="text-xs text-base-content/60">
                {resendAfterDelete
                  ? `They're prompted the next time they open Police Narratives AI. While ${data.agency.name}'s trial is still running they can choose "I'm still evaluating" to be asked again in 5 days.`
                  : "Otherwise they're asked again only when the survey next applies to them: the trial's final week, after it ends, or a department-wide send."}
              </p>
            </div>
          )
        }
        confirmLabel={resendAfterDelete ? "Delete and resend" : "Delete"}
        variant="danger"
        busy={busy}
        onConfirm={deleteResponses}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default AgencySurveyTab;
